'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import UiIcon from '@/components/ui/UiIcon';
import { homeProjectScopes } from '@/lib/homeProjectScopes';
import { usePublicProjects } from '@/lib/swr/hooks/useProjects';
import { directions, getDirectionProjects } from './directions';
import styles from './home-lab.module.css';

export function attachProjectRibbon(element: HTMLElement) {
  const phone = window.matchMedia('(max-width: 760px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0, timer = 0, last = 0, offset = 0, cycle = 0;
  let visible = false, hovered = false, focused = false, interacting = false;
  const stop = () => { cancelAnimationFrame(frame); frame = 0; last = 0; };
  const allowed = () => visible && phone.matches && !reduced.matches && !document.hidden && !hovered && !focused && !interacting;
  const wrap = () => {
    if (!cycle || focused) return;
    const next = cycle + ((element.scrollLeft % cycle) + cycle) % cycle;
    if (Math.abs(next - element.scrollLeft) > 1) element.scrollLeft = next;
    offset = element.scrollLeft;
  };
  const tick = (time: number) => {
    frame = 0;
    if (!allowed()) return;
    // Дробный остаток сохраняем отдельно: scrollLeft округляется на части устройств.
    if (last) offset += Math.min(time - last, 40) * .018;
    last = time;
    if (cycle && offset >= cycle * 2) offset -= cycle;
    element.scrollLeft = offset;
    frame = requestAnimationFrame(tick);
  };
  const sync = () => {
    stop();
    offset = element.scrollLeft;
    if (allowed()) frame = requestAnimationFrame(tick);
  };
  const enter = (event: PointerEvent) => { if (event.pointerType === 'mouse') { hovered = true; sync(); } };
  const leave = () => { hovered = false; sync(); };
  const focus = () => { focused = true; sync(); };
  const blur = (event: FocusEvent) => { if (!element.contains(event.relatedTarget as Node)) { focused = false; wrap(); sync(); } };
  const hold = () => { interacting = true; clearTimeout(timer); sync(); };
  const release = () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => { interacting = false; wrap(); sync(); }, 1800);
  };
  const wheel = () => { hold(); release(); };
  const scroll = () => { if (!frame) { wrap(); if (interacting) release(); } };
  const resize = new ResizeObserver(() => {
    const width = element.firstElementChild?.getBoundingClientRect().width || 0;
    if (width && width !== cycle) { cycle = width; element.scrollLeft = cycle; offset = cycle; }
    sync();
  });
  const intersection = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); });
  resize.observe(element);
  intersection.observe(element);
  element.addEventListener('pointerenter', enter);
  element.addEventListener('pointerleave', leave);
  element.addEventListener('focusin', focus);
  element.addEventListener('focusout', blur);
  element.addEventListener('pointerdown', hold);
  window.addEventListener('pointerup', release);
  window.addEventListener('pointercancel', release);
  element.addEventListener('wheel', wheel, { passive: true });
  element.addEventListener('scroll', scroll, { passive: true });
  document.addEventListener('visibilitychange', sync);
  phone.addEventListener('change', sync);
  reduced.addEventListener('change', sync);
  return () => {
    stop(); clearTimeout(timer); resize.disconnect(); intersection.disconnect();
    element.removeEventListener('pointerenter', enter);
    element.removeEventListener('pointerleave', leave);
    element.removeEventListener('focusin', focus);
    element.removeEventListener('focusout', blur);
    element.removeEventListener('pointerdown', hold);
    window.removeEventListener('pointerup', release);
    window.removeEventListener('pointercancel', release);
    element.removeEventListener('wheel', wheel);
    element.removeEventListener('scroll', scroll);
    document.removeEventListener('visibilitychange', sync);
    phone.removeEventListener('change', sync);
    reduced.removeEventListener('change', sync);
  };
}

export default function HeroProjectNavigation({ onDirection }: { onDirection: (chapter: number) => void }) {
  const ribbon = useRef<HTMLDivElement>(null);
  const [phone, setPhone] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const update = () => setPhone(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (phone && ribbon.current) return attachProjectRibbon(ribbon.current);
  }, [phone]);

  return <section className={styles.heroProjects} aria-label="Быстрый переход к проектам">
    <div ref={ribbon} className={styles.projectRibbon} aria-label="Проекты организаций">
      {(phone ? [0, 1, 2] : [1]).map(copy => <div key={copy} className={styles.projectRibbonGroup} aria-hidden={copy !== 1 || undefined}>
        {homeProjectScopes.map(item => <Link key={item.id} href={`/projects?organization=${item.id}`} prefetch={false}
          tabIndex={copy === 1 ? 0 : -1} className={styles.projectScope}>
          <UiIcon name={item.icon} /><span><small>{item.label}</small><strong>{item.name}</strong></span><UiIcon name="ArrowUpRight" />
        </Link>)}
      </div>)}
    </div>
    {phone && <div className={styles.heroDirectionGrid} aria-label="Направления проектов лаборатории">
      {directions.map((item, index) => <DirectionProjectButton key={item.id} direction={item} onClick={() => onDirection(index + 1)} />)}
    </div>}
  </section>;
}

function DirectionProjectButton({ direction, onClick }: { direction: typeof directions[number]; onClick: () => void }) {
  const feed = usePublicProjects(24, direction.id);
  const projects = getDirectionProjects(direction.id, feed.projects);
  const count = !projects.length && (feed.isLoading || feed.error) ? '—' : `${projects.length}${projects.length === 24 ? '+' : ''}`;
  return <button type="button" onClick={onClick} aria-label={`${direction.title}. Проектов: ${count}`}>
    <UiIcon name={direction.icon} /><span>{direction.title}</span>
    <span className={styles.heroProjectCount}>{count}<small>проектов</small></span>
  </button>;
}
